import React, { useState } from 'react';
import { Container, Box, Typography, Paper, Grid, Divider, Button, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';

// Import components
import FileUploader from '../components/FileUploader';
import FilePreview from '../components/FilePreview';
import InfoCard from '../components/InfoCard';

// Import context
import { useAuth } from '../contexts/AuthContext';

function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, canUpload } = useAuth();
  const [fileUrl, setFileUrl] = useState('');
  const [fileMetadata, setFileMetadata] = useState(null);
  const [originalFilename, setOriginalFilename] = useState('');
  const [hashFilename, setHashFilename] = useState('');
  const [ipfsGatewayUrl, setIpfsGatewayUrl] = useState('');

  // Handle successful file upload
  const handleUploadComplete = (result) => {
    console.log('Upload complete:', result);
    setFileUrl(result.url);
    setFileMetadata(result.metadata);
    setOriginalFilename(result.originalFilename);
    setHashFilename(result.hashFilename);
    setIpfsGatewayUrl(result.ipfsGatewayUrl);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Secure File Upload
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary">
          Store your files securely with Vercel Blob Storage and SHA-256 verification
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mt: 4,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Upload a File
        </Typography>

        <Typography variant="body1" paragraph>
          Select a file to upload to Vercel Blob Storage. Supports a wide range of file types including images, videos, audio,
          documents (PDF, DOC, DOCX, etc.), code/text files (JSON, HTML, CSS, JS, etc.), 3D models (including .vox voxel models),
          archives, and more. Files are verified with SHA-256 hashing. Maximum file size: 100MB.
        </Typography>

        {!isAuthenticated ? (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
              >
                Login
              </Button>
            }
          >
            Please login to upload files. Only Admin and Content Manager roles can upload.
          </Alert>
        ) : !canUpload() ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your account does not have permission to upload files. Contact an administrator to upgrade your role.
          </Alert>
        ) : (
          <FileUploader onUploadComplete={handleUploadComplete} />
        )}
      </Paper>

      {/* File Preview */}
      {fileUrl && (
        <FilePreview
          fileUrl={fileUrl}
          fileMetadata={fileMetadata}
          originalFilename={originalFilename}
          hashFilename={hashFilename}
          ipfsGatewayUrl={ipfsGatewayUrl}
        />
      )}

      {/* Two Column Layout for Desktop */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <InfoCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mt: { xs: 0, md: 4 },
              mb: 4,
              borderRadius: theme.shape.borderRadius,
            }}
          >
            <Typography variant="h5" gutterBottom>
              What is Vercel Blob Storage?
            </Typography>

            <Typography variant="body1" paragraph>
              Vercel Blob Storage is a secure, scalable cloud storage solution that provides fast,
              reliable file storage with global CDN delivery. Files are verified using SHA-256 hashing
              to ensure integrity and authenticity.
            </Typography>

            <Typography variant="body1" paragraph>
              Files are stored in Vercel's global infrastructure and served via a worldwide CDN for
              optimal performance. Each file is content-addressed using its SHA-256 hash, ensuring
              files can be verified and haven't been tampered with.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Key Benefits
            </Typography>

            <Typography variant="body1" component="div">
              <ul>
                <li>Global CDN for fast worldwide delivery</li>
                <li>SHA-256 hashing ensures file integrity and verification</li>
                <li>Automatic scaling and built-in redundancy</li>
                <li>Secure HTTPS delivery for all files</li>
                <li>Simple integration with Airtable metadata storage</li>
                <li>Native Vercel platform integration</li>
              </ul>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Home;
